/**
 * Global ⌘K / Ctrl-K command palette (§4.2).
 *
 * Built on `cmdk`. Renders into a Radix dialog so focus trapping and
 * escape-to-close come for free. Items are grouped by Navigate / Action
 * / Search; Enter runs, Esc closes.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  LayoutDashboard,
  Users,
  Activity as ActivityIcon,
  Settings as SettingsIcon,
  Server,
  Plus,
  LogOut,
  RotateCw,
  Search,
} from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useLogout } from '@/hooks/useAuth';
import { useUiStore } from '@/stores/uiStore';
import { cn } from '@/lib/utils';

export function CommandPalette() {
  const open = useUiStore((s) => s.commandPaletteOpen);
  const openPalette = useUiStore((s) => s.openCommandPalette);
  const closePalette = useUiStore((s) => s.closeCommandPalette);
  const navigate = useNavigate();
  const logout = useLogout();

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (open) closePalette();
        else openPalette();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, openPalette, closePalette]);

  const setOpen = (next: boolean) => {
    if (next) openPalette();
    else closePalette();
  };

  function run(fn: () => void) {
    return () => {
      closePalette();
      // Defer navigation so dialog close doesn't race the route change.
      requestAnimationFrame(fn);
    };
  }

  function handleLogout() {
    closePalette();
    logout.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 max-w-xl">
        <Command
          label="Command palette"
          className="rounded-md overflow-hidden"
          shouldFilter={true}
        >
          <div className="flex items-center border-b px-3">
            <Search className="h-4 w-4 text-muted-foreground mr-2" />
            <Command.Input
              placeholder="Type a command or search…"
              className={cn(
                'flex-1 h-11 bg-transparent outline-none text-sm',
                'placeholder:text-muted-foreground'
              )}
            />
            <kbd className="text-[10px] text-muted-foreground border rounded px-1.5 py-0.5 ml-2">
              esc
            </kbd>
          </div>
          <Command.List className="max-h-80 overflow-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              No results found.
            </Command.Empty>

            <Command.Group heading="Navigate" className="text-xs text-muted-foreground">
              <PaletteItem
                icon={LayoutDashboard}
                label="Dashboard"
                hint="Home"
                onSelect={run(() => navigate('/'))}
              />
              <PaletteItem
                icon={Users}
                label="Agents"
                onSelect={run(() => navigate('/agents'))}
              />
              <PaletteItem
                icon={ActivityIcon}
                label="Activity"
                onSelect={run(() => navigate('/activity'))}
              />
              <PaletteItem
                icon={Server}
                label="System"
                onSelect={run(() => navigate('/system'))}
              />
              <PaletteItem
                icon={SettingsIcon}
                label="Settings"
                onSelect={run(() => navigate('/settings'))}
              />
            </Command.Group>

            <Command.Group heading="Actions" className="text-xs text-muted-foreground">
              <PaletteItem
                icon={Plus}
                label="New agent"
                hint="Open wizard"
                onSelect={run(() => navigate('/agents/new'))}
              />
              <PaletteItem
                icon={RotateCw}
                label="Restart agent…"
                hint="Go to Agents list"
                onSelect={run(() => navigate('/agents'))}
              />
              <PaletteItem icon={LogOut} label="Log out" onSelect={handleLogout} />
            </Command.Group>

            <Command.Group heading="Search" className="text-xs text-muted-foreground">
              <Command.Item
                className="px-3 py-2 text-sm text-muted-foreground italic"
                disabled
              >
                Search across agents, conversations, audit log (coming soon)
              </Command.Item>
            </Command.Group>
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

interface PaletteItemProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  hint?: string;
  onSelect: () => void;
}

function PaletteItem({ icon: Icon, label, hint, onSelect }: PaletteItemProps) {
  return (
    <Command.Item
      onSelect={onSelect}
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-sm text-sm cursor-pointer',
        'aria-selected:bg-muted'
      )}
    >
      <Icon className="h-4 w-4" />
      <span className="flex-1">{label}</span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </Command.Item>
  );
}
